import { computed, Ref } from "vue";
import { PlanOption } from "@/features/planSelection/models/PlanOption";
import { createCurrencyFormatter } from "@/utils/formatCurrency";
import { BillingInterval } from "../types/BillingInterval";

/**
 * Pricing‑summary calculations split by charge bucket (one‑time, monthly, yearly).
 * Adds separate VAT figures for monthly and yearly so the component can render
 * formattedTaxOnMonthly and formattedTaxOnYearly props.
 */
export function usePricingSummary(
    plans: Ref<PlanOption[]>,
    billingInterval: Ref<BillingInterval>,
    options?: {
        taxRate?: number;
        locale?: string;
        currency?: string;
    },
) {
    const { taxRate = 0, locale = "nl-NL", currency = "EUR" } = options || {};
    const format = createCurrencyFormatter({ locale, currency });

    /* ---------- Raw totals per bucket ---------- */
    const oneTimeTotal = computed(() =>
        plans.value
            .filter((p) => p.isOneTime)
            .reduce((sum, p) => sum + p.price, 0),
    );
    const monthlyTotal = computed(() =>
        plans.value
            .filter((p) => p.isMonthly)
            .reduce((sum, p) => sum + p.price, 0),
    );

    const monthlyTotalForYear = computed(() => monthlyTotal.value * 12);
    const yearlyOnlyTotal = computed(() =>
        plans.value
            .filter((p) => p.isYearly)
            .reduce((sum, p) => sum + p.price, 0),
    );

    /* ---------- Helper values ---------- */
    const annualizedMonthly = computed(() => monthlyTotal.value * 12);
    const monthlyBase = computed(() =>
        billingInterval.value === "monthly" ? monthlyTotal.value : annualizedMonthly.value,
    );

    /* ---------- Recurring & VAT ---------- */
    const recurringTotal = computed(() =>
        billingInterval.value === "monthly"
            ? monthlyTotal.value + yearlyOnlyTotal.value
            : annualizedMonthly.value + yearlyOnlyTotal.value,
    );

    const taxOnOneTime = computed(() => oneTimeTotal.value * taxRate);
    const taxOnMonthly = computed(() => monthlyBase.value * taxRate);
    const taxOnYearly = computed(() => yearlyOnlyTotal.value * taxRate);
    const taxOnRecurring = computed(() => taxOnMonthly.value + taxOnYearly.value);

    /* ---------- Grand total ---------- */
    const totalDueNowRaw = computed(() => oneTimeTotal.value + recurringTotal.value);
    const totalDueNowWithTax = computed(() => totalDueNowRaw.value * (1 + taxRate));

    /* ---------- Formatted values ---------- */
    const formattedOneTime = computed(() => format(oneTimeTotal.value).formatted);
    const formattedMonthlyRow = computed(() =>
        format(monthlyBase.value).formatted,
    );
    const formattedYearlyRow = computed(() => format(yearlyOnlyTotal.value).formatted);
    const formattedRecurringRow = computed(() => format(recurringTotal.value).formatted);
    const annualRecurringTotalFormatted = computed(() => format(yearlyOnlyTotal.value + monthlyTotalForYear.value).formatted);


    const formattedTaxOnOneTime = computed(() => format(taxOnOneTime.value).formatted);
    const formattedTaxOnMonthly = computed(() => format(taxOnMonthly.value).formatted);
    const formattedTaxOnYearly = computed(() => format(taxOnYearly.value).formatted);
    const formattedTaxOnRecurring = computed(() => format(taxOnRecurring.value).formatted);

    const formattedTotalDueNow = computed(() => format(totalDueNowWithTax.value).formatted);

    /* ---------- Flags ---------- */
    const hasOneTime = computed(() => oneTimeTotal.value > 0);
    const hasMonthly = computed(() => monthlyTotal.value > 0);
    const hasYearly = computed(() => yearlyOnlyTotal.value > 0);

    return {
        // inputs
        billingInterval,
        taxRate,

        // formatted rows
        formattedOneTime,
        formattedMonthlyRow,
        formattedYearlyRow,
        formattedRecurringRow,
        formattedTaxOnOneTime,
        formattedTaxOnMonthly,
        formattedTaxOnYearly,
        formattedTaxOnRecurring,
        formattedTotalDueNow,
        annualRecurringTotalFormatted,

        // flags
        hasOneTime,
        hasMonthly,
        hasYearly,
    };
}
